import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { useContext, useEffect, useCallback, useRef, useState } from "react";
import { Token, DirectMessagesApi } from "@devour/client";
import { IStore } from "@/redux/defaultStore";
import getConfig from "@/utils/getConfig";
import { WebsocketProviderContext } from "@/components/WebsocketProvider";
import { useGetUserProfile } from "@/hooks/useGetUserProfile";

/**
 * Query keys for DM cache management
 */
export const DM_CONVERSATIONS_QUERY_KEY = "dm-conversations";
export const DM_MESSAGES_QUERY_KEY = "dm-messages";
export const DM_UNREAD_COUNT_QUERY_KEY = "dm-unread-count";

/**
 * WebSocket events for DMs
 * These match packages/models/src/WebsocketEvent.ts
 */
const DmEvents = {
    MESSAGE_NEW: "DM_MESSAGE_NEW",
    MESSAGE_READ: "DM_MESSAGE_READ",
    MESSAGE_DELETED: "DM_MESSAGE_DELETED",
    USER_TYPING: "DM_USER_TYPING",
    CONVERSATION_UPDATE: "DM_CONVERSATION_UPDATE",
    UNREAD_COUNT_UPDATE: "DM_UNREAD_COUNT_UPDATE",
} as const;

/**
 * Types matching backend responses
 */
export interface DmConversation {
    id: string;
    participantId: string;
    participantUsername: string;
    participantAvatarUrl?: string;
    isOnline?: boolean;
    lastMessageContent?: string;
    lastMessageAt?: number;
    lastMessageSenderId?: string;
    unreadCount: number;
    isMuted: boolean;
    isArchived: boolean;
    createdAt: number;
    updatedAt: number;
}

export interface DmMessage {
    id: string;
    conversationId: string;
    senderId: string;
    senderUsername: string;
    senderAvatarUrl?: string;
    recipientId: string;
    content: string;
    status: "SENT" | "DELIVERED" | "READ";
    createdAt: number;
    readAt?: number;
}

interface GetConversationsResponse {
    conversations: DmConversation[];
    unreadTotal: number;
    hasMore: boolean;
}

interface GetMessagesResponse {
    messages: DmMessage[];
    hasMore: boolean;
    oldestTimestamp?: number;
}

interface SendMessageResponse {
    message: DmMessage;
    conversationCreated: boolean;
}

/**
 * Fetch DM conversations with pagination
 */
async function fetchConversations(
    fullToken: Token,
    limit: number,
    offset: number,
    includeArchived: boolean = false,
): Promise<GetConversationsResponse> {
    const data = await new DirectMessagesApi(getConfig(fullToken)).getDmConversations({
        limit,
        offset,
        includeArchived,
    });
    return data as unknown as GetConversationsResponse;
}

/**
 * Fetch DM messages with pagination
 */
async function fetchMessages(
    fullToken: Token,
    userId: string,
    limit: number,
    before?: number,
): Promise<GetMessagesResponse> {
    const data = await new DirectMessagesApi(getConfig(fullToken)).getDmConversationsUserIdMessages({
        userId,
        limit,
        before,
    });
    return data as unknown as GetMessagesResponse;
}

/**
 * Fetch total unread DM count
 */
async function fetchUnreadCount(fullToken: Token): Promise<number> {
    const data = await new DirectMessagesApi(getConfig(fullToken)).getDmUnreadCount();
    return (data as any).count ?? 0;
}

/**
 * Send a DM message
 */
async function sendMessage(
    fullToken: Token,
    userId: string,
    content: string,
): Promise<SendMessageResponse> {
    const data = await new DirectMessagesApi(getConfig(fullToken)).createDmConversationsUserIdMessages({
        userId,
        sendDmMessageBody: {content} as any,
    });
    return data as unknown as SendMessageResponse;
}

/**
 * Mark conversation as read
 */
async function markConversationRead(fullToken: Token, userId: string): Promise<void> {
    await new DirectMessagesApi(getConfig(fullToken)).updateDmConversationsUserIdRead({userId});
}

/**
 * Update conversation settings
 */
async function updateSettings(
    fullToken: Token,
    userId: string,
    settings: { isMuted?: boolean; isArchived?: boolean; isBlocked?: boolean },
): Promise<void> {
    await new DirectMessagesApi(getConfig(fullToken)).updateDmConversationsUserIdSettings({
        userId,
        updateDmSettingsBody: settings as any,
    });
}

interface UseDmConversationsOptions {
    limit?: number;
    includeArchived?: boolean;
    enabled?: boolean;
}

interface UseDmConversationsReturn {
    conversations: DmConversation[];
    hasMore: boolean;
    isLoading: boolean;
    isFetchingMore: boolean;
    error: Error | null;
    unreadCount: number;
    isLoadingCount: boolean;
    loadMore: () => void;
    refetch: () => void;
    openConversation: (userId: string) => void;
    archiveConversation: (userId: string) => Promise<void>;
    muteConversation: (userId: string, muted: boolean) => Promise<void>;
    blockUser: (userId: string) => Promise<void>;
}

/**
 * Hook for managing DM conversations list
 *
 * Features:
 * - React Query for caching and background refetching
 * - WebSocket subscription for real-time updates
 * - Pagination with loadMore
 *
 * Usage:
 *   const {
 *     conversations,
 *     unreadCount,
 *     openConversation,
 *   } = useDmConversations();
 */
export function useDmConversations(options: UseDmConversationsOptions = {}): UseDmConversationsReturn {
    const { limit = 20, includeArchived = false, enabled = true } = options;

    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);
    const { data: userProfile } = useGetUserProfile(fullToken);
    const currentUserId = userProfile?.user?.id;
    const queryClient = useQueryClient();
    const { socket, socketConnection } = useContext(WebsocketProviderContext);

    // Track for open conversation navigation
    const [, setActiveConversationUserId] = useState<string | null>(null);

    // Main conversations query
    const {
        data: conversationsData,
        isLoading,
        isFetching,
        error,
        refetch,
    } = useQuery({
        queryKey: [DM_CONVERSATIONS_QUERY_KEY, fullToken?.id, limit, includeArchived],
        queryFn: () => fetchConversations(fullToken!, limit, 0, includeArchived),
        enabled: Boolean(fullToken) && enabled,
        staleTime: 2 * 60 * 1000, // 2 minutes
        refetchOnWindowFocus: false,
    });

    // Unread count query (separate for badge updates)
    const {
        data: unreadCount = 0,
        isLoading: isLoadingCount,
    } = useQuery({
        queryKey: [DM_UNREAD_COUNT_QUERY_KEY, fullToken?.id],
        queryFn: () => fetchUnreadCount(fullToken!),
        enabled: Boolean(fullToken) && enabled,
        staleTime: 30 * 1000, // 30 seconds
        refetchOnWindowFocus: true,
    });

    // Archive conversation mutation
    const archiveMutation = useMutation({
        mutationFn: async (userId: string) => {
            await updateSettings(fullToken!, userId, { isArchived: true });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [DM_CONVERSATIONS_QUERY_KEY] });
        },
    });

    // Mute conversation mutation
    const muteMutation = useMutation({
        mutationFn: async ({ userId, muted }: { userId: string; muted: boolean }) => {
            await updateSettings(fullToken!, userId, { isMuted: muted });
        },
        onMutate: async ({ userId, muted }) => {
            // Optimistic update
            await queryClient.cancelQueries({ queryKey: [DM_CONVERSATIONS_QUERY_KEY] });

            queryClient.setQueryData<GetConversationsResponse>(
                [DM_CONVERSATIONS_QUERY_KEY, fullToken?.id, limit, includeArchived],
                (old) => {
                    if (!old) return old;
                    return {
                        ...old,
                        conversations: old.conversations.map((c) =>
                            c.participantId === userId ? { ...c, isMuted: muted } : c),
                    };
                },
            );
        },
    });

    // Block user mutation
    const blockMutation = useMutation({
        mutationFn: async (userId: string) => {
            await updateSettings(fullToken!, userId, { isBlocked: true });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [DM_CONVERSATIONS_QUERY_KEY] });
        },
    });

    // Load more conversations (pagination)
    const loadMore = useCallback(async () => {
        if (!fullToken || !conversationsData || isFetching) return;

        const currentCount = conversationsData.conversations.length;
        if (!conversationsData.hasMore) return;

        const moreData = await fetchConversations(fullToken, limit, currentCount, includeArchived);

        queryClient.setQueryData<GetConversationsResponse>(
            [DM_CONVERSATIONS_QUERY_KEY, fullToken.id, limit, includeArchived],
            {
                ...conversationsData,
                conversations: [...conversationsData.conversations, ...moreData.conversations],
                hasMore: moreData.hasMore,
            },
        );
    }, [fullToken, conversationsData, isFetching, limit, includeArchived, queryClient]);

    // Open a conversation (sets active user for ChatThread)
    const openConversation = useCallback((userId: string) => {
        setActiveConversationUserId(userId);
    }, []);

    // WebSocket event handlers
    useEffect(() => {
        if (!socket || !socketConnection || !fullToken) return;

        // Handle new message (update conversation list)
        const handleNewMessage = (data: {
            conversationId: string;
            senderId: string;
            senderUsername: string;
            content: string;
            timestamp: string;
        }) => {
            // Don't increment unread if this is our own message
            const isOwnMessage = data.senderId === currentUserId;

            // Update conversation in list with new last message
            queryClient.setQueryData<GetConversationsResponse>(
                [DM_CONVERSATIONS_QUERY_KEY, fullToken.id, limit, includeArchived],
                (old) => {
                    if (!old) return old;

                    const existingIndex = old.conversations.findIndex((c) => c.id === data.conversationId);

                    if (existingIndex >= 0) {
                        // Move to top and update
                        const updated = [...old.conversations];
                        const [conv] = updated.splice(existingIndex, 1);
                        updated.unshift({
                            ...conv,
                            lastMessageContent: data.content,
                            lastMessageAt: new Date(data.timestamp).getTime(),
                            lastMessageSenderId: data.senderId,
                            // Only increment unread count for messages from others
                            unreadCount: isOwnMessage ? conv.unreadCount : conv.unreadCount + 1,
                        });
                        return { ...old, conversations: updated };
                    }

                    // New conversation - refetch to get full data
                    queryClient.invalidateQueries({ queryKey: [DM_CONVERSATIONS_QUERY_KEY] });
                    return old;
                },
            );
        };

        // Handle unread count update
        const handleUnreadCountUpdate = (data: { unreadCount: number }) => {
            queryClient.setQueryData([DM_UNREAD_COUNT_QUERY_KEY, fullToken.id], data.unreadCount);
        };

        // Handle conversation update (mute, archive, etc.)
        const handleConversationUpdate = (data: {
            conversationId: string;
            isMuted?: boolean;
            isArchived?: boolean;
        }) => {
            queryClient.setQueryData<GetConversationsResponse>(
                [DM_CONVERSATIONS_QUERY_KEY, fullToken.id, limit, includeArchived],
                (old) => {
                    if (!old) return old;
                    return {
                        ...old,
                        conversations: old.conversations.map((c) =>
                            c.id === data.conversationId
                                ? {
                                    ...c,
                                    isMuted: data.isMuted ?? c.isMuted,
                                    isArchived: data.isArchived ?? c.isArchived,
                                }
                                : c),
                    };
                },
            );
        };

        socket.on(DmEvents.MESSAGE_NEW, handleNewMessage);
        socket.on(DmEvents.UNREAD_COUNT_UPDATE, handleUnreadCountUpdate);
        socket.on(DmEvents.CONVERSATION_UPDATE, handleConversationUpdate);

        return () => {
            socket.off(DmEvents.MESSAGE_NEW, handleNewMessage);
            socket.off(DmEvents.UNREAD_COUNT_UPDATE, handleUnreadCountUpdate);
            socket.off(DmEvents.CONVERSATION_UPDATE, handleConversationUpdate);
        };
    }, [socket, socketConnection, fullToken, queryClient, limit, includeArchived, currentUserId]);

    const conversations = conversationsData?.conversations ?? [];
    const hasMore = conversationsData?.hasMore ?? false;

    return {
        conversations,
        hasMore,
        isLoading,
        isFetchingMore: isFetching && conversations.length > 0,
        error: error as Error | null,
        unreadCount,
        isLoadingCount,
        loadMore,
        refetch,
        openConversation,
        archiveConversation: archiveMutation.mutateAsync,
        muteConversation: (userId: string, muted: boolean) =>
            muteMutation.mutateAsync({ userId, muted }),
        blockUser: blockMutation.mutateAsync,
    };
}

interface UseDmMessagesOptions {
    userId: string;
    limit?: number;
    enabled?: boolean;
}

interface UseDmMessagesReturn {
    messages: DmMessage[];
    hasMore: boolean;
    isLoading: boolean;
    isFetchingMore: boolean;
    error: Error | null;
    loadMore: () => void;
    sendMessage: (content: string) => Promise<SendMessageResponse>;
    markAsRead: () => Promise<void>;
    refetch: () => void;
}

/**
 * Hook for managing messages in a DM conversation
 *
 * Usage:
 *   const {
 *     messages,
 *     sendMessage,
 *     loadMore,
 *   } = useDmMessages({ userId: "123" });
 */
export function useDmMessages(options: UseDmMessagesOptions): UseDmMessagesReturn {
    const { userId, limit = 50, enabled = true } = options;

    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);
    const queryClient = useQueryClient();
    const { socket, socketConnection } = useContext(WebsocketProviderContext);

    // Track oldest timestamp for pagination
    const oldestTimestampRef = useRef<number | undefined>(undefined);

    // Messages query
    const {
        data: messagesData,
        isLoading,
        isFetching,
        error,
        refetch,
    } = useQuery({
        queryKey: [DM_MESSAGES_QUERY_KEY, fullToken?.id, userId, limit],
        queryFn: () => fetchMessages(fullToken!, userId, limit),
        enabled: Boolean(fullToken) && Boolean(userId) && enabled,
        staleTime: 30 * 1000, // 30 seconds
        refetchOnWindowFocus: false,
    });

    // Send message mutation
    const sendMutation = useMutation({
        mutationFn: (content: string) => sendMessage(fullToken!, userId, content),
        onSuccess: (data) => {
            // Add new message to cache
            queryClient.setQueryData<GetMessagesResponse>(
                [DM_MESSAGES_QUERY_KEY, fullToken?.id, userId, limit],
                (old) => {
                    if (!old) return old;
                    return {
                        ...old,
                        messages: [...old.messages, data.message],
                    };
                },
            );

            // Update conversation list with new message
            queryClient.invalidateQueries({ queryKey: [DM_CONVERSATIONS_QUERY_KEY] });
        },
    });

    // Mark as read mutation
    const markReadMutation = useMutation({
        mutationFn: async () => {
            await markConversationRead(fullToken!, userId);
        },
        onSuccess: () => {
            // Invalidate unread count to trigger refetch
            queryClient.invalidateQueries({ queryKey: [DM_UNREAD_COUNT_QUERY_KEY] });

            /*
             * Invalidate conversations to update unread badge
             * Note: Using partial key invalidation since full key includes limit/includeArchived
             * which are not available in this hook
             */
            queryClient.invalidateQueries({
                queryKey: [DM_CONVERSATIONS_QUERY_KEY],
                exact: false,
            });
        },
    });

    // Load more (older) messages
    const loadMore = useCallback(async () => {
        if (!fullToken || !messagesData || isFetching || !messagesData.hasMore) return;

        const oldest = messagesData.oldestTimestamp;
        const moreData = await fetchMessages(fullToken, userId, limit, oldest);

        queryClient.setQueryData<GetMessagesResponse>(
            [DM_MESSAGES_QUERY_KEY, fullToken.id, userId, limit],
            {
                messages: [...moreData.messages, ...messagesData.messages],
                hasMore: moreData.hasMore,
                oldestTimestamp: moreData.oldestTimestamp,
            },
        );

        oldestTimestampRef.current = moreData.oldestTimestamp;
    }, [fullToken, messagesData, isFetching, limit, userId, queryClient]);

    // WebSocket event handlers for this conversation
    useEffect(() => {
        if (!socket || !socketConnection || !fullToken) return;

        // Handle new message in this conversation
        const handleNewMessage = (data: {
            id: string;
            conversationId: string;
            senderId: string;
            senderUsername: string;
            senderAvatarUrl?: string;
            recipientId: string;
            content: string;
            timestamp: string;
        }) => {
            // Only handle messages for this conversation
            if (data.senderId !== userId && data.recipientId !== userId) return;

            const newMessage: DmMessage = {
                id: data.id,
                conversationId: data.conversationId,
                senderId: data.senderId,
                senderUsername: data.senderUsername,
                senderAvatarUrl: data.senderAvatarUrl,
                recipientId: data.recipientId,
                content: data.content,
                status: "SENT",
                createdAt: new Date(data.timestamp).getTime(),
            };

            queryClient.setQueryData<GetMessagesResponse>(
                [DM_MESSAGES_QUERY_KEY, fullToken.id, userId, limit],
                (old) => {
                    if (!old) return { messages: [newMessage], hasMore: false };
                    // Avoid duplicates
                    if (old.messages.some((m) => m.id === data.id)) return old;
                    return {
                        ...old,
                        messages: [...old.messages, newMessage],
                    };
                },
            );
        };

        // Handle message read
        const handleMessageRead = (data: { conversationId: string; readBy: string; readAt: number }) => {
            queryClient.setQueryData<GetMessagesResponse>(
                [DM_MESSAGES_QUERY_KEY, fullToken.id, userId, limit],
                (old) => {
                    if (!old) return old;
                    return {
                        ...old,
                        messages: old.messages.map((m) =>
                            m.recipientId === data.readBy
                                ? { ...m, status: "READ" as const, readAt: data.readAt }
                                : m),
                    };
                },
            );
        };

        socket.on(DmEvents.MESSAGE_NEW, handleNewMessage);
        socket.on(DmEvents.MESSAGE_READ, handleMessageRead);

        return () => {
            socket.off(DmEvents.MESSAGE_NEW, handleNewMessage);
            socket.off(DmEvents.MESSAGE_READ, handleMessageRead);
        };
    }, [socket, socketConnection, fullToken, queryClient, userId, limit]);

    const messages = messagesData?.messages ?? [];
    const hasMore = messagesData?.hasMore ?? false;

    return {
        messages,
        hasMore,
        isLoading,
        isFetchingMore: isFetching && messages.length > 0,
        error: error as Error | null,
        loadMore,
        sendMessage: sendMutation.mutateAsync,
        markAsRead: markReadMutation.mutateAsync,
        refetch,
    };
}

export default useDmConversations;
